/**
 * Centralized layout constants for consistent spacing across the application.
 *
 * These values ensure visual consistency between Header, Sidebar, and MainLayout.
 * When modifying these values, all dependent components will update automatically.
 */

// =============================================================================
// HEADER
// =============================================================================

/**
 * App bar (header) height in pixels.
 * 48px (h-12) is a compact, modern height used by apps like Slack and Notion.
 * Previous value was 64px (h-16).
 */
export const HEADER_HEIGHT = 48;

/**
 * Tailwind class for header height.
 * Must match HEADER_HEIGHT value.
 */
export const HEADER_HEIGHT_CLASS = 'h-12';

// =============================================================================
// SIDEBAR
// =============================================================================

/**
 * Sidebar width in pixels (expanded state).
 */
export const SIDEBAR_WIDTH = 240;

/**
 * Sidebar width in pixels (collapsed state - icon rail).
 */
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// =============================================================================
// SINGLE-PANEL SIDEBAR (Module dropdown + nav)
// =============================================================================

/**
 * Sidebar width when collapsed (icons only).
 * w-14 (56px) - compact but touch-friendly.
 */
export const SIDEBAR_PANEL_COLLAPSED_WIDTH = 56;

/**
 * Sidebar width when expanded on hover (showing labels).
 * w-56 (224px) - enough for nav item text + module dropdown.
 */
export const SIDEBAR_PANEL_EXPANDED_WIDTH = 224;

/**
 * @deprecated Use SIDEBAR_PANEL_COLLAPSED_WIDTH instead.
 * Kept for backwards compatibility during migration.
 */
export const MODULE_RAIL_WIDTH = SIDEBAR_PANEL_COLLAPSED_WIDTH;

/**
 * @deprecated Use SIDEBAR_PANEL_COLLAPSED_WIDTH instead.
 * Total sidebar width when collapsed — now a single panel.
 */
export const SIDEBAR_TWO_TIER_COLLAPSED_WIDTH = SIDEBAR_PANEL_COLLAPSED_WIDTH;

// =============================================================================
// MAIN CONTENT PADDING
// =============================================================================

/**
 * Main content top padding that accounts for fixed header.
 * Calculated as: header height (48px) + bottom spacing (16px) = 64px
 */
export const MAIN_CONTENT_TOP_PADDING_CLASS = 'pt-16';

/**
 * Responsive horizontal padding for main content area.
 * Scales with viewport: 16px (mobile) → 24px (sm) → 32px (lg) → 48px (xl)
 *
 * Best practice: More space on larger screens improves readability and reduces
 * the feeling of content being cramped against edges.
 */
export const MAIN_CONTENT_HORIZONTAL_PADDING_CLASS = 'px-4 sm:px-6 lg:px-8 xl:px-12';

/**
 * Responsive vertical padding for page content (non-fullHeight mode).
 */
export const PAGE_CONTENT_VERTICAL_PADDING_CLASS = 'py-4 sm:py-6';

// =============================================================================
// PAGE WIDTH DEFAULTS
// =============================================================================

/**
 * Default max-width for PageContainer.
 * 'full' (no constraint) is the modern approach - let content use available space.
 * Only constrain width for specific content types:
 * - 'lg' for forms, settings, text-heavy pages
 * - 'xl' for dashboards with cards
 */
export const PAGE_DEFAULT_MAX_WIDTH = 'full' as const;

/**
 * Max-width options for specific page types that need constraints.
 * Most pages should use the default (full width).
 */
export const PAGE_MAX_WIDTHS = {
  /** Forms, settings, text-heavy pages - 1024px */
  form: 'lg',
  /** Dashboards with stat cards - 1280px (optional, can use full) */
  dashboard: 'xl',
} as const;

// =============================================================================
// FULL HEIGHT CALCULATIONS
// =============================================================================

/**
 * Layout padding in pixels (top + bottom from MainLayout).
 * Used for fullHeight calculations in PageContainer.
 * 16px top gap (below header) + 24px bottom (pb-6) = 40px
 */
export const LAYOUT_VERTICAL_PADDING = 40;

/**
 * Total offset for full-height content.
 * Accounts for fixed header height + layout padding.
 */
export const FULL_HEIGHT_OFFSET = HEADER_HEIGHT + LAYOUT_VERTICAL_PADDING;
